import { gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import { Button } from '@apollo/orbit';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { OperationDetailsDrawer } from 'src/app/graph/operationsPage/operationDetails/OperationDetailsDrawer';
import { SaveCollectionEntryFromOperationsPaneModal } from 'src/app/graph/operationsPage/saveCollectionEntryFromOperationsPaneModal/SaveCollectionEntryFromOperationsPaneModal';
import {
  statsWindowFromValidationWindow,
  statsWindowFromValidationWindowOperationsCheckFragment,
} from 'src/components/affectedOperations/affectedOperationsHelpers';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { URLModal } from 'src/components/common/URLModal';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useBulkSelect } from 'src/hooks/useBulkSelect';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CheckWorkflowTaskStatus } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import {
  ProposalCheckRerunButtonWorkflowFragment,
  ProposalsCheckRerunButton,
} from '../../proposalChecksRerunButton/ProposalChecksRerunButton';

import { ChangeListChangeOnOperationFragment } from './changeList/ChangeList';
import { CheckEncounteredError } from './checkEncounteredError/CheckEncounteredError';
import { NoAffectedOperations } from './noAffectedOperations/NoAffectedOperations';
import { NoOperationsToCheckAgainstContent } from './noOperationsToCheckAgainstContent/NoOperationsToCheckAgainstContent';
import { OperationCardinalityExceeded } from './operationCardinalityExceeded/OperationCardinalityExceeded';
import { OperationCheckDetails } from './operationCheckDetails/OperationCheckDetails';
import { OperationCheckDetailsHeader } from './OperationCheckDetailsHeader';
import {
  AffectedOperation,
  OperationsCheckList,
  UnaffectedOperation,
  getOperationStatus,
} from './operationsCheckList/OperationsCheckList';
import { OperationsListBreadcrumb } from './operationsCheckList/OperationsListBreadcrumbs';
import { searchOperations } from './operationsCheckList/searchOperations';
import { ProposalsAlertBanner } from './proposalsAlertBanner/ProposalsAlertBanner';

export const AffectedQueryDetailsFragment = gql`
  fragment AffectedQueryDetailsFragment on AffectedQuery {
    id
    name
    isValid
    markedAsSafe
    markedAsIgnored
    alreadyApproved
    alreadyIgnored
  }
`;

const OperationCheckDetailsAffectedQueryFragment = gql`
  fragment OperationCheckDetailsAffectedQueryFragment on AffectedQuery {
    id
    changes {
      ...ChangeListChangeOnOperationFragment
    }
    ...AffectedQueryDetailsFragment
  }
  ${AffectedQueryDetailsFragment}
  ${ChangeListChangeOnOperationFragment}
`;

const OperationCheckDetailsQuery = gql`
  query OperationCheckDetailsQuery(
    $graphId: ID!
    $checkId: ID!
    $isProposalRoute: Boolean!
  ) {
    service(id: $graphId) {
      id
      checkWorkflow(id: $checkId) {
        id
        ...StatsWindowFromValidationWindowOperationsCheckFragment
        ...ProposalCheckRerunButtonWorkflowFragment
        operationsTask {
          id
          status
          result {
            id
            crossedOperationThreshold
            changes {
              category
              code
            }
            checkedVariant {
              id
              name
              usageLastReportedAt
              proposal @include(if: $isProposalRoute) {
                id
                hasUpstreamChanges
              }
            }
            numberOfCheckedOperations
            numberOfAffectedOperations
            totalNumberOfAffectedOperations
            affectedQueries {
              id
              ...AffectedQueryDetailsFragment
            }
            unaffectedOperations {
              id
              name
            }
          }
        }
      }
    }
  }
  ${statsWindowFromValidationWindowOperationsCheckFragment}
  ${ProposalCheckRerunButtonWorkflowFragment}
  ${AffectedQueryDetailsFragment}
`;

const AffectedQueryDetailsQuery = gql`
  query AffectedQueryDetailsQuery(
    $graphId: ID!
    $workflowId: ID!
    $operationId: ID!
  ) {
    service(id: $graphId) {
      id
      checkWorkflow(id: $workflowId) {
        id
        operationsTask {
          id
          result {
            id
            affectedQuery(id: $operationId) {
              id
              ...OperationCheckDetailsAffectedQueryFragment
            }
          }
        }
      }
    }
  }
  ${OperationCheckDetailsAffectedQueryFragment}
`;

export function OperationCheckResults() {
  const history = useHistory();
  const location = useLocation();
  const proposalRebaseBeforePublish = useLDFlag('gov-rebase-before-publish');
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const { currentOperationsCheckDetailsRoute, isProposalRoute } =
    useInternalGraphLinking();

  const {
    checkId,
    graphId,
    graphVariant,
    query: selectedOperationId,
    overlay,
    page,
    statusFilter,
    search,
  } = useRouteParams(currentOperationsCheckDetailsRoute);

  const { data, loading, error } = useQuery<
    GraphQLTypes.OperationCheckDetailsQuery,
    GraphQLTypes.OperationCheckDetailsQueryVariables
  >(OperationCheckDetailsQuery, {
    variables: {
      graphId,
      checkId,
      isProposalRoute,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    /**
     * There is actually a data model error in that the thing we call
     * AffectedQuery is currently identified by the operation ID.  However,
     * the same operation ID can appear in multiple checks,
     * but with different safety markers.  This makes it impossible to use a
     * normalized cache for an AffectedQuery, so always hit the network here.
     */
    fetchPolicy: 'cache-and-network',
  });

  const workflow = data?.service?.checkWorkflow;
  const task = workflow?.operationsTask;
  const proposal = task?.result?.checkedVariant.proposal;

  const { data: affectedQueryData, loading: affectedQueryLoading } = useQuery<
    GraphQLTypes.AffectedQueryDetailsQuery,
    GraphQLTypes.AffectedQueryDetailsQueryVariables
  >(AffectedQueryDetailsQuery, {
    variables: {
      graphId,
      workflowId: workflow?.id as string,
      operationId: selectedOperationId as string,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    skip: selectedOperationId === undefined || !workflow,
  });

  const affectedQuery =
    affectedQueryData?.service?.checkWorkflow?.operationsTask?.result
      ?.affectedQuery;

  const [operationForDrawer, setOperationForDrawer] = useState<
    AffectedOperation | UnaffectedOperation
  >();

  const allOperations = useMemo(
    () =>
      task?.__typename === 'OperationsCheckTask'
        ? [
            ...(task.result?.affectedQueries || []),
            ...(task.result?.unaffectedOperations || []),
          ]
        : [],
    [task],
  );
  const searchedAndFilteredResults = searchOperations(
    allOperations,
    search ?? '',
  ).filter((operation) => {
    const operationStatus = getOperationStatus(operation);
    return statusFilter?.length
      ? statusFilter.includes(operationStatus)
      : operation;
  });

  const { bulkSelectState, dispatchBulkSelectAction } = useBulkSelect<string>();

  if (overlay && !selectedOperationId && !operationForDrawer) {
    return (
      <Redirect
        to={currentOperationsCheckDetailsRoute.locationFrom({
          location,
          patch: {
            overlay: RouteConfig.DELETE,
          },
        })}
      />
    );
  } else if (loading) {
    return <Loading />;
  } else if (error) {
    return (
      <div className="p-2">
        <ErrorMessage error={error} />
      </div>
    );
  } else if (!workflow || task?.__typename !== 'OperationsCheckTask') {
    return <NotFound />;
  }

  const status = task.status;
  const result = task.result;
  const hasChanges = (result?.changes?.length || 0) > 0;
  const hasOperations = !!result?.numberOfCheckedOperations;

  const totalNumberOfOperations =
    (result?.totalNumberOfAffectedOperations ?? 0) +
    (result?.unaffectedOperations?.length ?? 0);

  if (
    status === CheckWorkflowTaskStatus.FAILED &&
    !hasChanges &&
    !hasOperations
  ) {
    // If the check failed but there are no changes and no operations, it means
    // that something went wrong while the check was running
    return <CheckEncounteredError />;
  } else if (
    result?.crossedOperationThreshold &&
    result?.totalNumberOfAffectedOperations === 0
  ) {
    // Show a cardinality error if we went over the limit of operations
    return (
      <OperationCardinalityExceeded
        crossedOperationThreshold={result.crossedOperationThreshold || 0}
      />
    );
  } else if (result && !hasOperations) {
    // Show empty state if no operations were checked
    return (
      <NoOperationsToCheckAgainstContent
        failedStaticCheck={
          status === CheckWorkflowTaskStatus.FAILED &&
          isMinLengthArray(1, result?.changes)
        }
        hasUsageData={!!result.checkedVariant.usageLastReportedAt}
        hasChanges={hasChanges}
        graphId={graphId}
        graphVariant={result?.checkedVariant.name ?? 'current'}
      />
    );
  } else {
    // Otherwise show the operations list (even if all affected queries are marked
    // as safe or ignored)
    const selectedOperationIdIndex =
      searchedAndFilteredResults.findIndex(
        (op) => op.id === selectedOperationId,
      ) ?? -1;

    if (
      overlay &&
      selectedOperationId &&
      !operationForDrawer &&
      affectedQuery
    ) {
      setOperationForDrawer(affectedQuery);
    }

    const statsWindow = statsWindowFromValidationWindow(
      currentPlan.currentPlan,
      workflow ?? null,
    );
    const nextOperation =
      selectedOperationIdIndex >= 0
        ? searchedAndFilteredResults[selectedOperationIdIndex + 1]
        : undefined;

    return (
      <>
        {result ? (
          <>
            {!selectedOperationId && (
              <div className="flex justify-between">
                <OperationCheckDetailsHeader
                  numberOfAffectedOperations={
                    result?.numberOfAffectedOperations
                  }
                  numberOfCheckedOperations={result?.numberOfCheckedOperations}
                  createdAt={workflow.createdAt}
                  from={workflow.validationConfig?.from}
                  to={workflow.validationConfig?.to}
                />
                {workflow.proposalRevision?.id && graphVariant && (
                  <ProposalsCheckRerunButton
                    graphId={graphId}
                    graphVariant={graphVariant}
                    proposalRevisionId={workflow.proposalRevision.id}
                    checkWorkflowId={workflow.id}
                    trigger={
                      <Button
                        size="md"
                        variant="secondary"
                        rightIcon={<IconChevronDown />}
                      >
                        Rerun check
                      </Button>
                    }
                  />
                )}
              </div>
            )}{' '}
            {!proposalRebaseBeforePublish &&
              isProposalRoute &&
              proposal?.hasUpstreamChanges &&
              result?.changes?.some(
                (change) => change.code === 'FIELD_REMOVED',
              ) && <ProposalsAlertBanner />}
            {result?.numberOfAffectedOperations === 0 &&
            (!result?.unaffectedOperations ||
              result?.unaffectedOperations?.length === 0) &&
            result?.totalNumberOfAffectedOperations === 0 ? (
              <NoAffectedOperations />
            ) : (
              <div className="flex h-0 min-h-80 flex-1 flex-col">
                <OperationsListBreadcrumb
                  operationId={affectedQuery?.id}
                  operationName={affectedQuery?.name || 'Unnamed Operation'}
                  onViewOperationBodyClick={() => {
                    if (affectedQuery) {
                      setOperationForDrawer(affectedQuery);
                    }
                  }}
                  nextOperationId={
                    nextOperation?.__typename === 'AffectedQuery'
                      ? nextOperation.id
                      : undefined
                  }
                  prevOperationId={
                    selectedOperationIdIndex > 0
                      ? searchedAndFilteredResults[selectedOperationIdIndex - 1]
                          ?.id
                      : undefined
                  }
                  createdAt={workflow.createdAt}
                  from={workflow.validationConfig?.from}
                  to={workflow.validationConfig?.to}
                />

                {affectedQueryLoading ? (
                  <Loading />
                ) : affectedQuery ? (
                  <OperationCheckDetails
                    graphId={graphId}
                    graphVariant={result?.checkedVariant.name ?? 'current'}
                    checkId={task.id}
                    workflowId={checkId}
                    affectedQuery={affectedQuery}
                    affectedQueryLoading={affectedQueryLoading}
                    statsWindow={statsWindow}
                    canViewClientVersions={
                      currentPlan.currentPlan?.clientVersions ?? false
                    }
                  />
                ) : (
                  <OperationsCheckList
                    pageFromUrl={page}
                    search={search}
                    statusFilter={statusFilter}
                    allOperations={allOperations}
                    searchedAndFilteredResults={searchedAndFilteredResults}
                    setOperationForDrawer={(operation) => {
                      setOperationForDrawer(operation);
                      history.push({
                        ...setOverlayVisibleLocation({
                          location,
                          overlayName: 'operation-details',
                        }),
                      });
                    }}
                    graphId={graphId}
                    bulkSelectState={bulkSelectState}
                    dispatchBulkSelectAction={dispatchBulkSelectAction}
                    checkId={task.id}
                    numberOfAffectedOperations={
                      result?.numberOfAffectedOperations
                    }
                    totalNumberOfOperations={totalNumberOfOperations}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <CheckEncounteredError />
        )}
        <URLModal name={Config.modals.saveMetricsOperationToCollection}>
          {({ isOpen, closeModal }) => (
            <SaveCollectionEntryFromOperationsPaneModal
              isOpen={isOpen}
              query={selectedOperationId ?? ''}
              onClose={closeModal}
            />
          )}
        </URLModal>
        <OperationDetailsDrawer
          graphId={graphId}
          graphVariant={result?.checkedVariant.name ?? 'current'}
          operationId={operationForDrawer?.id}
          queryName={operationForDrawer?.name}
          overlay={overlay}
        />
      </>
    );
  }
}
